import { useEffect, useMemo, useRef, useState } from "react";

import { easeOutExpo } from "../utils/siteUtils";

/**
 * Animate numeric UI as the element enters the viewport.
 * Translation: Animate numeric UI as the element enters the viewport.
 *
 * @param {number} targetValue - Final numeric value.
 * @param {Object} options - Presentation options.
 * @returns {Object} Display value, progress, and the observed ref.
 */
const useCountUpOnView = (
  targetValue,
  {
    duration = 2500,
    prefix = "",
    suffix = "",
    decimals = 0,
    enabled = true,
  } = {}
) => {
  const elementRef = useRef(null);
  const [progress, setProgress] = useState(enabled ? 0 : 1);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [enabled, targetValue]);

  useEffect(() => {
    if (!enabled || !hasStarted) {
      return;
    }

    let animationFrame = 0;
    let startTime = 0;

    /**
     * Progress the numeric animation frame-by-frame.
     * Translation: Progress the numeric animation frame-by-frame.
     *
     * @param {number} timestamp - Animation frame timestamp.
     */
    const animate = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const nextProgress = Math.min(elapsed / duration, 1);
      setProgress(nextProgress);

      if (nextProgress < 1) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };

    animationFrame = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [duration, enabled, hasStarted]);

  const currentNumber = useMemo(() => {
    const easedProgress = enabled ? easeOutExpo(progress) : 1;
    return targetValue * easedProgress;
  }, [enabled, progress, targetValue]);

  const displayValue = useMemo(() => {
    const formattedNumber = currentNumber.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return `${prefix}${formattedNumber}${suffix}`;
  }, [currentNumber, decimals, prefix, suffix]);

  return {
    elementRef,
    displayValue,
    progress: enabled ? progress : 1,
  };
};

export default useCountUpOnView;
